import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Calendar } from '@enfomentis/ui';
import { today, getLocalTimeZone, parseDate, isWeekend } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import { Calendar as CalendarIcon, TrendingUp, AlertTriangle, Users, Globe, Target } from 'lucide-react';

export const CalendarExamples: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<DateValue | null>(today(getLocalTimeZone()));
  const [tradingDate, setTradingDate] = useState<DateValue | null>(null);
  const [focusedDate, setFocusedDate] = useState<DateValue>(today(getLocalTimeZone()));

  // Custom date availability logic for trading calendar
  const isTradingDay = (date: DateValue) => {
    // Mark weekends as unavailable for trading
    return isWeekend(date, 'en-US');
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
      <div className="space-y-4">
        <h1 className="text-3xl font-bold">Calendar Component</h1>
        <p className="text-lg text-muted-foreground">
          Flexible date selection with internationalization support and custom availability rules.
        </p>
      </div>

      {/* Basic Usage */}
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Basic Usage</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Default Calendar"
              />
              <p className="text-sm text-text-secondary text-center">Default calendar with today highlighted</p>
            </div>
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Calendar with default value"
                defaultValue={parseDate("2024-12-25")}
              />
              <p className="text-sm text-text-secondary text-center">Calendar with Christmas pre-selected</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Color Variants */}
      <Card className="max-w-6xl mx-auto">
        <CardHeader>
          <h3 className="text-lg font-semibold">Color Variants</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-col xl:flex-row gap-6 items-center justify-center">
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Primary Calendar"
                color="primary"
                defaultValue={today(getLocalTimeZone())}
              />
              <p className="text-sm text-text-secondary text-center">Primary</p>
            </div>
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Secondary Calendar"
                color="secondary"
                defaultValue={today(getLocalTimeZone())}
              />
              <p className="text-sm text-text-secondary text-center">Secondary</p>
            </div>
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Success Calendar"
                color="success"
                defaultValue={today(getLocalTimeZone())}
              />
              <p className="text-sm text-text-secondary text-center">Success</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Date Constraints */}
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Date Constraints & Validation</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Calendar with min/max dates"
                minValue={today(getLocalTimeZone())}
                maxValue={today(getLocalTimeZone()).add({ months: 3 })}
                defaultValue={today(getLocalTimeZone()).add({ days: 7 })}
                color="warning"
              />
              <p className="text-sm text-text-secondary text-center">Limited to next 3 months from today</p>
            </div>
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Calendar with unavailable weekends"
                defaultValue={today(getLocalTimeZone())}
                isDateUnavailable={isTradingDay}
                color="danger"
              />
              <p className="text-sm text-text-secondary text-center">Weekends marked as unavailable</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Multiple Months */}
      <Card className="max-w-7xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Multiple Month Views</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-6">
            <div className="space-y-2">
              <Calendar 
                aria-label="Two month calendar"
                visibleMonths={2}
                defaultValue={today(getLocalTimeZone())}
                color="primary"
              />
              <p className="text-sm text-text-secondary text-center">Two months side by side</p>
            </div>
            
            <div className="space-y-2">
              <Calendar 
                aria-label="Three month calendar"
                visibleMonths={3}
                defaultValue={today(getLocalTimeZone())}
                color="secondary"
              />
              <p className="text-sm text-text-secondary text-center">Three months for planning ahead</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Enhanced Features */}
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Enhanced Features</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Calendar with month/year pickers"
                showMonthAndYearPickers
                defaultValue={today(getLocalTimeZone())}
                color="primary"
              />
              <p className="text-sm text-text-secondary text-center">With month and year picker dropdowns</p>
            </div>
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Read-only calendar"
                isReadOnly
                defaultValue={parseDate("2024-07-04")}
                color="foreground"
              />
              <p className="text-sm text-text-secondary text-center">Read-only calendar (display only)</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Controlled State */}
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Controlled State</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Controlled selection"
                value={selectedDate}
                onChange={setSelectedDate}
                color="primary"
              />
              <p className="text-sm text-text-secondary text-center">
                Selected: {selectedDate ? selectedDate.toString() : 'None'}
              </p>
            </div>
            <div className="space-y-2 flex flex-col items-center">
              <Calendar 
                aria-label="Controlled focus"
                focusedValue={focusedDate}
                onFocusChange={setFocusedDate}
                color="secondary"
              />
              <p className="text-sm text-text-secondary text-center">
                Focused: {focusedDate.toString()}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Trading Application Example */}
      <Card className="max-w-6xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Trading Application Example</h3>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Calendar 
                  aria-label="Select trading date"
                  value={tradingDate}
                  onChange={setTradingDate}
                  minValue={today(getLocalTimeZone())}
                  maxValue={today(getLocalTimeZone()).add({ months: 6 })}
                  isDateUnavailable={isTradingDay}
                  color="success"
                  showMonthAndYearPickers
                />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Trading Date Selection</p>
                  <p className="text-sm text-text-secondary">
                    Selected: {tradingDate ? tradingDate.toString() : 'Select a trading day'}
                  </p>
                  <p className="text-xs text-text-tertiary">
                    • Weekends are unavailable for trading
                    • Future dates only (up to 6 months)
                    • Quick month/year navigation enabled
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-surface rounded-lg border border-border">
                  <h4 className="font-semibold mb-2">Calendar Features</h4>
                  <ul className="text-sm text-text-secondary space-y-1">
                    <li>• Business days only (weekends disabled)</li>
                    <li>• Future date constraints</li>
                    <li>• International date support</li>
                    <li>• Keyboard navigation</li>
                    <li>• Customizable availability rules</li>
                    <li>• Multiple month views</li>
                    <li>• Rapid date navigation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      </div>
    </PlaygroundPageLayout>
  );
};